import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { useShellTranslation } from '../navigation/useShellTranslation';
import { useTrialStatus } from '../navigation/useTrialStatus';
import { formatTrialEnd, trialFeatureFor } from '../navigation/trialChipModel';

const { signal, radius } = tokens;

interface TrialFeatureBadgeProps {
  /** The boolean `PlanLimits` key the feature is sold under, e.g. `rebateTracking`. */
  feature: string;
  /**
   * Nav rows: no tooltip (the row is a link, and a hover card on it gets in
   * the way), just the tier name.
   */
  compact?: boolean;
}

/**
 * The tier a trial feature belongs to ("Pro", "Intelligence", "Complete"),
 * shown while a store's trial runs on a feature it loses when the trial ends
 * (MOT-729, flag `store_trial_countdown`). Renders nothing for a feature the
 * store keeps, and outside a trial.
 */
export const TrialFeatureBadge: React.FC<TrialFeatureBadgeProps> = ({ feature, compact = false }) => {
  const { t } = useShellTranslation();
  const { i18n } = useTranslation();
  const data = useTrialStatus();
  const trial = data?.trial ?? null;
  const entry = trialFeatureFor(trial, feature, Date.now());
  if (!trial || !entry) return null;

  const badge = (
    <Box
      component="span"
      data-testid={`trial-feature-badge-${feature}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        // Nav rows are ~90px of label beside it, and "Intelligence" is the
        // longest tier name, so the nav badge is a size down.
        height: compact ? 16 : 20,
        px: compact ? '5px' : '7px',
        border: `1px solid ${signal.info.border}`,
        background: signal.info.tint,
        color: signal.info.ink,
        borderRadius: `${radius.pill}px`,
        fontSize: compact ? '10px' : '11px',
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      {entry.tierName}
    </Box>
  );
  if (compact) return badge;

  const tooltip = trial.endsAt
    ? t('shell.trial.featureBadge.tooltip', {
        plan: trial.planName,
        tier: entry.tierName,
        date: formatTrialEnd(trial.endsAt, i18n.language),
      })
    : t('shell.trial.featureBadge.tooltipNoEnd', { plan: trial.planName, tier: entry.tierName });

  return (
    <Tooltip title={tooltip} arrow>
      <Box component="span" tabIndex={0} aria-label={tooltip} sx={{ display: 'inline-flex', borderRadius: `${radius.pill}px` }}>
        {badge}
      </Box>
    </Tooltip>
  );
};

/** A section or page title with the trial tier badge beside it. */
export const TrialFeatureTitle: React.FC<{ title: React.ReactNode; feature: string }> = ({ title, feature }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
    <span>{title}</span>
    <TrialFeatureBadge feature={feature} />
  </Box>
);
